import { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { generatePath } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import SweetAlert from 'sweetalert2'
import withReactContent from 'sweetalert2-react-content'

import { ReactComponent as AddSVG } from 'assets/images/add.svg'

import { Flex } from 'components/flex'
import { PageLoading } from 'components/pageLoading'
import { Button } from 'components/button'
import { Table } from 'components/table'
import { ContestByStatus } from './components/contestByStatus'

import { withAdminLayout } from 'hoc/withAdminLayout'

import { browserHistory } from 'navigation/browserHistory'
import { CONTEST_CREATE_PAGE } from 'navigation/paths'

import { GlobalState } from 'store'
import { getData } from 'store/dashboard/actions'
import { searchUserContests } from 'store/contest/actions'

import { confirmationConfig } from 'config/swal'
import { useQuery } from 'hooks/useQuery'

import { UserType } from 'types/enums/UserType'
import { TableColumn } from 'types/table/TableColumn'
import { ContestSearchResult } from 'types/contest/ContestSearchResult'

import { DashboardWrapper, DashboardWidget } from './styled'
import { formatDateBR } from 'utils/formatUtils'
import { ContestStep } from 'types/enums/ContestStep'
import { ErrorItem, OkItem, WarningItem } from 'pages/contestList/styled'

function Dashboard(): JSX.Element {
  const { t } = useTranslation(['pages', 'common'])
  const dispatch = useDispatch()
  const query = useQuery()

  const {
    contest: {
      contestData,
      searchUserContestsResult,
      searchUserContestsInProgress
    },
    session: { user: sessionUser },
    dashboard: { data, getDataInProgress }
  } = useSelector((state: GlobalState) => state)

  document.title = 'Dashboard - Open Insurance'

  function _canCreateContest(): boolean {
    return sessionUser?.type === UserType.DIRECTORY
  }

  const contestTableColumns = useMemo<TableColumn<ContestSearchResult>[]>(
    () => [
      {
        label: t('pages:contestList.table.id'),
        dataKey: 'id',
        sort: true,
        thStyle: { width: '80px' },
        tdStyle: { width: '80px' },
        render: (row) => <strong>{row.id}</strong>
      },
      {
        label: t('pages:contestList.table.requester'),
        dataKey: 'requester',
        sort: true,
        thStyle: { minWidth: '60px', paddingRight: 25 },
        tdStyle: { minWidth: '60px', paddingLeft: 30 }
      },
      {
        label: t('pages:contestList.table.defendant'),
        dataKey: 'defendant',
        sort: true,
        thStyle: { minWidth: '60px', paddingRight: 25 },
        tdStyle: { minWidth: '60px', paddingLeft: 30 }
      },
      {
        label: t('pages:contestList.table.createdAt'),
        dataKey: 'createdAt',
        sort: true,
        thStyle: { minWidth: '40px', paddingRight: 25 },
        tdStyle: { minWidth: '40px', paddingLeft: 30 },
        render: (row) => formatDateBR(row.createdAt)
      },
      {
        label: t('pages:contestList.table.currentStep'),
        dataKey: 'currentStep',
        sort: true,
        thStyle: {
          minWidth: '150px',
          paddingLeft: 30,
          paddingRight: 25
        },
        tdStyle: {
          minWidth: '50px',
          justifyContent: 'center'
        },
        render: (row) => {
          if (
            row.currentStep.toUpperCase() === ContestStep.CANCELED ||
            row.currentStep.toUpperCase() === ContestStep.EXPIRED ||
            row.currentStep.toUpperCase() === ContestStep.ABORTED
          ) {
            return (
              <ErrorItem>
                <label>{t(`common:contestStep.${row.currentStep}`)}</label>
              </ErrorItem>
            )
          } else if (
            row.currentStep.toUpperCase() === ContestStep.OPEN ||
            row.currentStep.toUpperCase() === ContestStep.ANSWER ||
            row.currentStep.toUpperCase() ===
              ContestStep.ELIGIBLE_FOR_ARBITRATION ||
            row.currentStep.toUpperCase() === ContestStep.FINISHED ||
            row.currentStep.toUpperCase() === ContestStep.RESOLVED_D ||
            row.currentStep.toUpperCase() === ContestStep.RESOLVED_R ||
            row.currentStep.toUpperCase() ===
              ContestStep.RESOLVED_WITHOUT_SIGNATURE ||
            row.currentStep.toUpperCase() ===
              ContestStep.RESOLVED_WITH_SIGNATURE
          ) {
            return (
              <OkItem>
                <label>{t(`common:contestStep.${row.currentStep}`)}</label>
              </OkItem>
            )
          } else {
            return (
              <WarningItem>
                <label>{t(`common:contestStep.${row.currentStep}`)}</label>
              </WarningItem>
            )
          }
        }
      }
    ],
    [contestData]
  )

  useEffect(() => {
    if (sessionUser) {
      dispatch(
        getData({
          start: '2021-01-01',
          end: '9999-01-01'
        })
      )
    }
  }, [])

  useEffect(() => {
    if (sessionUser?.id) {
      const page = parseInt(query.get('page') ?? '1')

      if (page < 1) {
        return
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const advanced: any = {}

      if (query.get('requester')?.length) {
        advanced.requester = query.get('requester')
      }

      if (query.get('defendant')?.length) {
        advanced.defendant = query.get('defendant')
      }

      if (query.get('step')?.length) {
        advanced.step = query.get('step')
      }

      dispatch(
        searchUserContests(sessionUser?.id, {
          page: page,
          limit: 10,
          orderBy: query.get('orderBy') ?? 'id',
          order: query.get('order') ?? 'desc',
          side: query.get('side') ?? undefined,
          targetDate: query.get('targetDate') ?? undefined,
          fromDate: query.get('fromDate') ?? undefined,
          toDate: query.get('toDate') ?? undefined,
          advanced: JSON.stringify(advanced)
        })
      )
    }
  }, [query.toString()])

  const totalContests = useMemo(
    () =>
      data
        ? Number(data.openDefendant.count) + Number(data.openRequester.count)
        : 0,
    [data]
  )
  const onGoingContests = useMemo(
    () =>
      data
        ? Number(data.onGoingDefendant.count) +
          Number(data.onGoingRequester.count)
        : 0,
    [data]
  )
  const requesterContests = useMemo(
    () => (data ? Number(data.openRequester.count) : 0),
    [data]
  )
  const finishedContests = useMemo(
    () =>
      data
        ? Number(data.finishedDefendant.count) +
          Number(data.finishedRequester.count)
        : 0,
    [data]
  )

  console.log(data)

  return (
    <DashboardWrapper>
      {getDataInProgress && (
        <PageLoading
          style={{ position: 'absolute', inset: 0, marginBottom: 42 }}
        />
      )}
      <Flex direction="row" align="center" justify="space-between">
        <Flex direction="column" gap={1}>
          <h1 style={{ marginBottom: 0 }}>Seja bem-vindo</h1>
          <p>Verique suas atualizações</p>
        </Flex>

        {_canCreateContest() && (
          <Flex direction="row" justify="flex-end">
            <Button
              backgroundColor="primary"
              type="button"
              small
              onClick={() =>
                withReactContent(SweetAlert)
                  .fire({
                    ...confirmationConfig,
                    icon: 'warning',
                    titleText: t('pages:contestList.confirmNewContest.title'),
                    html: t('pages:contestList.confirmNewContest.message'),
                    confirmButtonText: t(
                      'pages:contestList.confirmNewContest.confirmButton'
                    ),
                    cancelButtonText: t(
                      'pages:contestList.confirmNewContest.cancelButton'
                    )
                  })
                  .then(({ isConfirmed }) => {
                    if (isConfirmed) {
                      browserHistory.push(generatePath(CONTEST_CREATE_PAGE))
                    }
                  })
              }
            >
              <AddSVG />
              Nova disputa
            </Button>
          </Flex>
        )}
      </Flex>
      <Flex
        direction="row"
        align="center"
        justify="space-between"
        style={{ padding: '0 50px' }}
      >
        <Flex
          direction="row"
          align="center"
          justify="center"
          wrap={true}
          gap={8}
          style={{ maxWidth: 564 }}
        >
          <DashboardWidget
            primaryColor="#3C3C3C"
            secondaryColor="#3C3C3C"
            textColor="#FFFFFF"
          >
            <h2>{totalContests}</h2>
            <h3>Disputas totais</h3>
          </DashboardWidget>
          <DashboardWidget
            primaryColor="#BDE8E54F"
            secondaryColor="#159F9F"
            textColor="#3B3B3B"
          >
            <h2>{onGoingContests}</h2>
            <h3>Disputas em andamento</h3>
          </DashboardWidget>
          <DashboardWidget
            primaryColor="#FFE39917"
            secondaryColor="#FFE399"
            textColor="#3B3B3B"
          >
            <h2>{requesterContests}</h2>
            <h3>Disputas requeridas</h3>
          </DashboardWidget>
          <DashboardWidget
            primaryColor="#B6D7B436"
            secondaryColor="#3FA73A"
            textColor="#3B3B3B"
          >
            <h2>{finishedContests}</h2>
            <h3>Disputas encerradas</h3>
          </DashboardWidget>
        </Flex>
        <Flex
          direction="column"
          align="center"
          justify="center"
          style={{
            position: 'relative',
            width: 309,
            height: 284,
            borderRadius: 20,
            backgroundColor: 'white'
          }}
        >
          <strong
            style={{ position: 'absolute', top: 15, left: 15, fontSize: 18 }}
          >
            Disputas
          </strong>
          <ContestByStatus
            data={[
              { count: onGoingContests, name: 'Disputas em andamento' },
              { count: requesterContests, name: 'Disputas requeridas' },
              { count: finishedContests, name: 'Disputas encerradas' }
            ]}
          />
        </Flex>
      </Flex>
      <Flex direction="row" align="center" justify="space-between">
        <Table
          columns={contestTableColumns}
          data={searchUserContestsResult?.data ?? []}
          pageCount={searchUserContestsResult?.pagination.lastPage}
          isLoading={searchUserContestsInProgress}
          disableTopMargin={true}
          headerChildren={
            <h3 style={{ marginTop: 15 }}>
              {t('pages:contestList.breadcrumb.contest')}
            </h3>
          }
        />
      </Flex>
    </DashboardWrapper>
  )
}

export const DashboardPage = withAdminLayout(Dashboard)
